/**
 * Per-article ledger — a unified, timestamp-sorted event feed + P&L summary
 * for the catalog right pane. Backed by GET /api/catalog/articles/{id}/ledger.
 *
 * Compact 2-line layout (the pane is narrow): a left date gutter, then the
 * type badge + info on line 1, with qty / Pfand / value stacked underneath in
 * smaller text. Sort / type-filter / date-filter live in a toolbar above (the
 * collapsed single-column table has no header to host them). Sales aggregate
 * per day by default, toggleable to granular (one row per purchase, buyer
 * nickname linking to the user's tab).
 */

import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  DatePicker,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

import {
  ApiError,
  api,
  type ArticleLedgerResponse,
  type LedgerRow,
  type LedgerSource,
} from "../api/client";
import { formatCents } from "../utils/dateFormat";

const { Text } = Typography;

const SOURCE_META: Record<LedgerSource, { color: string; label: string }> = {
  procurement: { color: "blue", label: "Procurement" },
  sale: { color: "green", label: "Sale" },
  stocktake: { color: "purple", label: "Stocktake" },
  stock_loss: { color: "red", label: "Stock loss" },
  adjustment: { color: "orange", label: "Adjustment" },
  correction: { color: "gold", label: "Correction" },
  stocktake_reverted: { color: "default", label: "Stocktake reverted" },
};

const POS = "#16a34a";
const NEG = "#cf1322";

type SortKey =
  | "newest"
  | "oldest"
  | "value_desc"
  | "value_asc"
  | "qty_desc"
  | "qty_asc";

/** Numeric compare with nulls pushed to the bottom. */
const numCmp = (a: number | null, b: number | null) =>
  (b ?? Number.NEGATIVE_INFINITY) - (a ?? Number.NEGATIVE_INFINITY);

function coloured(c: number | null, withEuro: boolean) {
  if (c == null) return <Text type="secondary">—</Text>;
  const text = withEuro ? formatCents(c) : c > 0 ? `+${c}` : String(c);
  return (
    <Text style={{ color: c < 0 ? NEG : c > 0 ? POS : undefined }}>{text}</Text>
  );
}

/** Line-1 info: the buyer for a sale (count when aggregated), else the row's
 * label — wrapped in the right Link. Skips repeating the type word (the badge
 * already shows it). */
function blockInfo(row: LedgerRow) {
  if (row.source === "sale") {
    if (row.buyer_name != null && row.buyer_id != null)
      return <Link to={`/users/${row.buyer_id}`}>{row.buyer_name}</Link>;
    if (row.buyer_count != null)
      return (
        <Text type="secondary">
          {row.buyer_count} buyer{row.buyer_count === 1 ? "" : "s"}
        </Text>
      );
    return <Text type="secondary">—</Text>;
  }
  const link = row.link;
  let label: React.ReactNode = row.label;
  if (link?.type === "procurement")
    label = <Link to={`/stock/procurement/${link.id}`}>{row.label}</Link>;
  else if (link?.type === "stocktake")
    label = <Link to={`/stock/stocktakes/${link.id}`}>{row.label}</Link>;
  else if (link?.type === "expense")
    label = <Link to="/stock/expenses">{row.label}</Link>;
  const buyer =
    row.source === "correction" && row.buyer_id != null ? (
      <Link to={`/users/${row.buyer_id}`}> · #{row.buyer_id}</Link>
    ) : null;
  return (
    <span>
      {label}
      {buyer}
    </span>
  );
}

function EventBlock({ row }: { row: LedgerRow }) {
  // Daily sale aggregates + date-only stock-loss carry an artificial noon
  // timestamp — show the date only, no misleading time.
  const dateOnly =
    (row.source === "sale" && row.buyer_count != null) || row.source === "stock_loss";
  const d = dayjs(row.timestamp);
  return (
    <div style={{ display: "flex", gap: 10 }}>
      <div style={{ width: 66, flexShrink: 0, lineHeight: 1.25 }}>
        <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
          {d.format("DD.MM.YYYY")}
        </Text>
        {!dateOnly && (
          <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
            {d.format("HH:mm")}
          </Text>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Space size={6} wrap style={{ rowGap: 2 }}>
          <Tag color={SOURCE_META[row.source].color} style={{ marginInlineEnd: 0 }}>
            {SOURCE_META[row.source].label}
          </Tag>
          <span style={{ minWidth: 0 }}>{blockInfo(row)}</span>
        </Space>
        <div
          style={{
            fontSize: 12,
            marginTop: 2,
            display: "flex",
            gap: 12,
            flexWrap: "wrap",
            color: "var(--ant-color-text-secondary, #8c8c8c)",
          }}
        >
          <span>Qty {coloured(row.qty, false)}</span>
          <span>Pfand {coloured(row.pfand_cents, true)}</span>
          <span>Value {coloured(row.value_cents, true)}</span>
        </div>
      </div>
    </div>
  );
}

function SummaryStrip({ s }: { s: ArticleLedgerResponse["summary"] }) {
  const item = (label: string, value: string, strong = false, color?: string) => (
    <span>
      <Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Text>{" "}
      <Text strong={strong} style={{ color }}>
        {value}
      </Text>
    </span>
  );
  return (
    <Space size="large" wrap style={{ marginBottom: 12 }}>
      {item("Revenue", formatCents(s.revenue_cents))}
      {item("Units", String(s.units_sold))}
      {item("Goods", formatCents(s.goods_cost_cents))}
      {item("Pfand in", formatCents(s.pfand_in_cents))}
      {item("Stock loss", formatCents(s.stock_loss_cents))}
      {item("Corrections", formatCents(s.corrections_cents))}
      {item("Profit", formatCents(s.profit_cents), true, s.profit_cents < 0 ? NEG : POS)}
    </Space>
  );
}

export function ArticleLedger({ articleId }: { articleId: number }) {
  const [aggregate, setAggregate] = useState<"daily" | "granular">("daily");
  const [data, setData] = useState<ArticleLedgerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Toolbar (client-side) sort + filters over the fetched rows. ``typeFilter``
  // is INCLUDE semantics — empty = show all; pick types to show ONLY those.
  const [sortKey, setSortKey] = useState<SortKey>("newest");
  const [typeFilter, setTypeFilter] = useState<LedgerSource[]>([]);
  const [dateFilter, setDateFilter] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .getArticleLedger(articleId, { aggregate })
      .then((r) => {
        if (!cancelled) setData(r);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [articleId, aggregate]);

  const displayed = useMemo(() => {
    let rows = data?.rows ?? [];
    if (typeFilter.length) rows = rows.filter((r) => typeFilter.includes(r.source));
    if (dateFilter)
      rows = rows.filter((r) => dayjs(r.timestamp).format("YYYY-MM-DD") === dateFilter);
    const out = [...rows];
    switch (sortKey) {
      case "oldest":
        out.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
        break;
      case "value_desc":
        out.sort((a, b) => numCmp(a.value_cents, b.value_cents));
        break;
      case "value_asc":
        out.sort((a, b) => numCmp(b.value_cents, a.value_cents));
        break;
      case "qty_desc":
        out.sort((a, b) => numCmp(a.qty, b.qty));
        break;
      case "qty_asc":
        out.sort((a, b) => numCmp(b.qty, a.qty));
        break;
      default: // newest
        out.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
    }
    return out;
  }, [data, typeFilter, dateFilter, sortKey]);

  return (
    <div>
      {data && <SummaryStrip s={data.summary} />}
      <Space wrap size={[8, 8]} style={{ marginBottom: 12 }}>
        <Segmented
          size="small"
          value={aggregate}
          onChange={(v) => setAggregate(v as "daily" | "granular")}
          options={[
            { label: "Daily", value: "daily" },
            { label: "Granular", value: "granular" },
          ]}
        />
        <Select<SortKey>
          size="small"
          value={sortKey}
          onChange={setSortKey}
          style={{ width: 140 }}
          options={[
            { value: "newest", label: "Newest first" },
            { value: "oldest", label: "Oldest first" },
            { value: "value_desc", label: "Value ↓" },
            { value: "value_asc", label: "Value ↑" },
            { value: "qty_desc", label: "Qty ↓" },
            { value: "qty_asc", label: "Qty ↑" },
          ]}
        />
        <Select<LedgerSource[]>
          size="small"
          mode="multiple"
          allowClear
          placeholder="Show only…"
          value={typeFilter}
          onChange={(v) => setTypeFilter(v)}
          style={{ minWidth: 150 }}
          options={(Object.keys(SOURCE_META) as LedgerSource[]).map((s) => ({
            value: s,
            label: SOURCE_META[s].label,
          }))}
        />
        <DatePicker
          size="small"
          allowClear
          format="DD.MM.YYYY"
          value={dateFilter ? dayjs(dateFilter) : null}
          onChange={(d) => setDateFilter(d ? d.format("YYYY-MM-DD") : null)}
        />
        {data?.sales_truncated && (
          <Tooltip title="Switch to Daily for full totals — the summary above is already complete.">
            <Text type="warning" style={{ fontSize: 12 }}>
              showing most recent sales of {data.total_sales_count}
            </Text>
          </Tooltip>
        )}
      </Space>
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />}
      <Table<LedgerRow>
        showHeader={false}
        size="small"
        loading={loading}
        dataSource={displayed}
        columns={[{ key: "event", render: (_: unknown, row) => <EventBlock row={row} /> }]}
        rowKey={(r) => `${r.source}-${r.id}`}
        pagination={{
          defaultPageSize: 25,
          size: "small",
          showSizeChanger: true,
          pageSizeOptions: ["10", "25", "50", "100"],
        }}
      />
    </div>
  );
}
